import { ChevronDownIcon } from '@chakra-ui/icons'
import {
  Box,
  Collapse,
  Flex,
  HStack,
  IconButton,
  ResponsiveValue,
  Text,
} from '@chakra-ui/react'
import React, { useMemo, useRef, useState } from 'react'
import { PhoneStatus, TrackedParkingLot } from '~/atoms/WebphoneState'
import { QuickActionButtonSizes } from '~/components/shared/QuickActionButton'
import { AudioSettings } from '~/pages/WebPhone/AudioSettings'
import {
  CallControlButton,
  ControlButtonTypes,
} from '~/pages/WebPhone/CallControlButton'
import { Keypad } from '~/pages/WebPhone/KeyPad'
import { ParkControl } from '~/pages/WebPhone/ParkControl'
import { TransferControl } from '~/pages/WebPhone/TransferControl'
import { Size } from '~/types/ThemeTypes'

import { ExtensionPreview } from '@mangovoice/types'

import { useCallControlStyle } from './componentStyles/CallControl.style'

interface ControlDrawerHeaderProps {
  onCancelClick: () => void
  title?: string
}
const ControlDrawerHeader = ({
  onCancelClick: handleCancelClick,
  title,
}: ControlDrawerHeaderProps) => {
  const style = useCallControlStyle()
  return (
    <Flex sx={style.drawerHeader}>
      <Text sx={style.drawerHeaderText}>{title ?? '‎'}</Text>
      <IconButton
        sx={style.drawerHeaderIcon}
        aria-label="Close Drawer"
        onClick={handleCancelClick}
      >
        <ChevronDownIcon sx={style.drawerHeaderIcon} />
      </IconButton>
    </Flex>
  )
}

type SelectedControlState = 'KEYPAD' | 'AUDIO' | 'PARK' | 'TRANSFER'

interface CallControlProps {
  callStatus: PhoneStatus
  size: ResponsiveValue<QuickActionButtonSizes>
  isBelowMaxCallLimit: boolean
  isInConference: boolean
  isRinging: boolean
  isMuted: boolean
  onMuteClick: () => void
  isOnHold: boolean
  onHoldClick: () => void
  onParkCall: (parkExtension: string) => void
  onTransferCall: (target: string) => void
  onDialButtonClick: (digit: string) => void
  audioInputs: MediaDeviceInfo[]
  selectedInput: string
  onInputDeviceChange: (deviceId: string) => void
  audioOutputs: MediaDeviceInfo[]
  selectedOutput: string
  onOutputDeviceChange: (deviceId: string) => void
  pbxExtensions: ExtensionPreview[]
  parkingLots: TrackedParkingLot[]
  onCallStop: () => void
  isControlDrawerOpen: boolean
  onMergeCalls: () => void
  onSplitConference: () => void
  onNewCall: () => void
  toggleDrawer: (isActive: boolean, overrideIsOpen?: boolean) => void
}
export const CallControl = ({
  callStatus,
  size,
  isBelowMaxCallLimit,
  isInConference,
  isRinging,
  isMuted,
  onMuteClick,
  isOnHold,
  onHoldClick,
  onParkCall,
  onTransferCall,
  onDialButtonClick,
  audioInputs,
  selectedInput,
  onInputDeviceChange,
  audioOutputs,
  selectedOutput,
  onOutputDeviceChange,
  pbxExtensions,
  parkingLots,
  isControlDrawerOpen,
  onCallStop,
  onMergeCalls,
  onSplitConference,
  onNewCall,
  toggleDrawer,
}: CallControlProps) => {
  const styles = useCallControlStyle({ size })
  const [openControlState, setOpenControlState] =
    useState<SelectedControlState>('KEYPAD')

  const barRef = useRef<HTMLDivElement>(null)

  const isKeyPadActive = openControlState === 'KEYPAD'
  const isAudioSettingsActive = openControlState === 'AUDIO'
  const isParkControlActive = openControlState === 'PARK'
  const isTransferControlActive = openControlState === 'TRANSFER'

  const handleKeyPadControlClick = () => {
    toggleDrawer(isKeyPadActive)
    setOpenControlState('KEYPAD')
  }
  const handleAudioSettingsClick = () => {
    toggleDrawer(isAudioSettingsActive)
    setOpenControlState('AUDIO')
  }
  const handleParkControlClick = () => {
    toggleDrawer(isParkControlActive)
    setOpenControlState('PARK')
  }
  const handleTransferControlClick = () => {
    toggleDrawer(isTransferControlActive)
    setOpenControlState('TRANSFER')
  }
  const handleCancelClick = () => {
    toggleDrawer(false, false)
  }
  const handleMergeCallsClick = () => {
    onMergeCalls()
  }
  const handleSplitConferenceClick = () => {
    onSplitConference()
  }
  const handleNewCallClick = () => {
    onNewCall()
  }

  const conferenceButton = useMemo(() => {
    if (isInConference) {
      return (
        <CallControlButton
          type={ControlButtonTypes.SPLIT}
          size={size}
          onClick={handleSplitConferenceClick}
        />
      )
    }
    if (isBelowMaxCallLimit) {
      return (
        <CallControlButton
          type={ControlButtonTypes.NEW}
          size={size}
          onClick={handleNewCallClick}
        />
      )
    }
    return (
      <CallControlButton
        type={ControlButtonTypes.MERGE}
        size={size}
        onClick={handleMergeCallsClick}
      />
    )
  }, [isInConference, isBelowMaxCallLimit, size])

  const getEstablishedExclusiveState = (
    controlType: SelectedControlState | 'HOLD',
    disabled?: boolean,
    active?: boolean,
  ) => {
    if (isRinging || disabled) {
      return { isActive: false, isDisabled: true }
    }
    if ((openControlState === controlType && isControlDrawerOpen) || active) {
      return { isActive: true, isDisabled: false }
    }
    return { isActive: false, isDisabled: false }
  }

  const keypadState = {
    isActive: isKeyPadActive && isControlDrawerOpen,
    isDisabled: false,
  }
  const audioSettingsState = {
    isActive: isAudioSettingsActive && isControlDrawerOpen,
    isDisabled: false,
  }
  const parkControlState = getEstablishedExclusiveState('PARK', isInConference)
  const transferControlState = getEstablishedExclusiveState(
    'TRANSFER',
    isInConference,
  )
  const muteState = { isActive: isMuted, isDisabled: false }
  const holdState = getEstablishedExclusiveState('HOLD', undefined, isOnHold)

  const barHeight = barRef.current?.offsetHeight ?? 0

  return (
    <>
      {/* Position and zIndex allow the drawer to display over other elements
       without disrupting flow */}
      <HStack ref={barRef} sx={styles.bar} position="relative">
        <Flex
          position="absolute"
          bottom={barHeight}
          left="50%"
          transform="translate(-50%, 4px)"
          direction="column"
          alignItems="center"
          justifyContent="flex-end"
          zIndex="2"
        >
          <Collapse
            animateOpacity={false}
            startingHeight="0"
            in={isControlDrawerOpen}
          >
            <Box __css={styles.drawer} zIndex="2">
              {isKeyPadActive && isControlDrawerOpen && (
                <>
                  <ControlDrawerHeader onCancelClick={handleCancelClick} />
                  <Flex paddingBottom="28px">
                    <Keypad
                      size={{ base: Size.sm, xl: Size.md }}
                      onButtonClick={onDialButtonClick}
                    />
                  </Flex>
                </>
              )}
              {isAudioSettingsActive && (
                <>
                  <ControlDrawerHeader
                    title="Audio Settings"
                    onCancelClick={handleCancelClick}
                  />
                  <AudioSettings
                    inputs={audioInputs}
                    selectedInput={selectedInput}
                    onInputDeviceChange={onInputDeviceChange}
                    outputs={audioOutputs}
                    selectedOutput={selectedOutput}
                    onOutputDeviceChange={onOutputDeviceChange}
                  />
                </>
              )}
              {isParkControlActive && (
                <>
                  <ControlDrawerHeader
                    title="Park Call"
                    onCancelClick={handleCancelClick}
                  />
                  <ParkControl
                    parkingLots={parkingLots}
                    onParkCall={onParkCall}
                  />
                </>
              )}
              {isTransferControlActive && (
                <>
                  <ControlDrawerHeader
                    title="Transfer Call"
                    onCancelClick={handleCancelClick}
                  />
                  <TransferControl
                    onTransfer={onTransferCall}
                    extensions={pbxExtensions}
                  />
                </>
              )}
            </Box>
          </Collapse>
        </Flex>

        <CallControlButton
          type={ControlButtonTypes.MUTE}
          size={size}
          onClick={onMuteClick}
          isActive={muteState.isActive}
          disabled={muteState.isDisabled}
        />
        <CallControlButton
          type={ControlButtonTypes.HOLD}
          size={size}
          onClick={onHoldClick}
          isActive={holdState.isActive}
          disabled={holdState.isDisabled}
        />
        <CallControlButton
          type={ControlButtonTypes.KEYPAD}
          size={size}
          onClick={handleKeyPadControlClick}
          isActive={keypadState.isActive}
          disabled={keypadState.isDisabled}
        />
        <CallControlButton
          type={ControlButtonTypes.AUDIO}
          size={size}
          onClick={handleAudioSettingsClick}
          isActive={audioSettingsState.isActive}
          disabled={audioSettingsState.isDisabled}
        />
        <CallControlButton
          type={ControlButtonTypes.PARK}
          size={size}
          onClick={handleParkControlClick}
          isActive={parkControlState.isActive}
          disabled={parkControlState.isDisabled}
        />
        <CallControlButton
          type={ControlButtonTypes.TRANSFER}
          size={size}
          onClick={handleTransferControlClick}
          isActive={transferControlState.isActive}
          disabled={transferControlState.isDisabled}
        />
        {conferenceButton}
        <CallControlButton
          type={ControlButtonTypes.END}
          size={size}
          onClick={onCallStop}
          disabled={
            callStatus === PhoneStatus.TRANSPORT_DISCONNECTED ||
            callStatus === PhoneStatus.ERROR ||
            callStatus === PhoneStatus.MIC_DENIED
          }
        />
      </HStack>
    </>
  )
}
